import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Search, X, User } from "lucide-react";

export default function UserSearchDropdown({ 
  users = [], 
  value, 
  onChange, 
  placeholder = "Search by name or email...",
  selectedLabel = ""
}) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState(users);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      setFilteredUsers(
        users.filter(u => 
          u.name?.toLowerCase().includes(searchLower) || 
          u.email?.toLowerCase().includes(searchLower)
        )
      );
    } else {
      setFilteredUsers(users);
    }
  }, [search, users]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (user) => {
    onChange(user.id, user.name);
    setSearch("");
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange("", "");
    setSearch("");
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={isOpen ? search : (selectedLabel || search)}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="pl-9 pr-8 rounded-lg dark:bg-slate-700 dark:border-slate-600"
          data-testid="user-search-input"
        />
        {(value || search) && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredUsers.length === 0 ? (
            <div className="p-3 text-sm text-slate-500 dark:text-slate-400 text-center">
              No users found
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div
                key={user.id}
                onClick={() => handleSelect(user)}
                className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${
                  value === user.id ? 'bg-brillien-50 dark:bg-brillien-900/30' : ''
                }`}
                data-testid={`user-option-${user.id}`}
              >
                <div className="h-8 w-8 rounded-full bg-brillien-100 dark:bg-brillien-900/50 flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-brillien-600 dark:text-brillien-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                    {user.name}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {user.email}
                  </p>
                </div>
                {value === user.id && (
                  <span className="text-brillien-600 dark:text-brillien-400 text-xs font-medium">
                    Selected
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
